/**
 * One category list for Add Vendor, Manage Categories, CSV import,
 * and the vendor self-signup form.
 * Settlement-service names are not offered. Existing vendor rows in those
 * categories are kept; homeowner and AI reads drop them in vendorAccess.
 * A stored type of "financial" does not hide a home-service category.
 */

const DEFAULT_VENDOR_CATEGORIES = [
  'HVAC',
  'Plumbing',
  'Electrical',
  'Appliance Repair',
  'Garage Door',
  'Roofing',
  'Gutters',
  'Painting',
  'Flooring',
  'Drywall',
  'Windows and Doors',
  'Hurricane Shutters',
  'Stucco',
  'Siding',
  'Insulation',
  'Handyman',
  'General Contractor',
  'House Cleaning',
  'Window Cleaning',
  'Pressure Washing',
  'Carpet Cleaning',
  'Air Duct Cleaning',
  'Dryer Vent Cleaning',
  'Chimney',
  'Landscaping',
  'Tree Service',
  'Irrigation',
  'Pool Service',
  'Pest Control',
  'Fencing',
  'Concrete and Pavers',
  'Dock and Seawall',
  'Screen Enclosures',
  'Septic',
  'Well and Water Treatment',
  'Mold Remediation',
  'Water Damage Restoration',
  'Solar',
  'Generator',
  'Moving',
  'Junk Removal',
  'Home Security',
  'Smart Home',
  'Locksmith',
  'Real Estate',
];

// CSV spellings that should land on a category above. Matching is exact
// after looseCategoryKey, not a substring search.
const CATEGORY_SYNONYMS = {
  HVAC: ['air conditioning', 'air conditioner', 'a/c', 'ac', 'a c', 'heating and air', 'heating and cooling', 'hvac service'],
  Plumbing: ['plumber', 'plumbers'],
  Electrical: ['electrician', 'electricians', 'electric'],
  'Appliance Repair': ['appliances', 'appliance'],
  'Garage Door': ['garage doors', 'garage door repair'],
  Roofing: ['roof', 'roofer', 'roofers', 'roof repair'],
  Gutters: ['gutter', 'gutter cleaning', 'gutter repair'],
  Painting: ['painter', 'painters', 'paint'],
  Flooring: ['floors', 'floor', 'hardwood', 'hardwood floors', 'tile'],
  Drywall: ['dry wall', 'sheetrock'],
  'Windows and Doors': ['window and door'],
  'Hurricane Shutters': ['hurricane shutters/impact windows', 'impact windows', 'impact window', 'storm shutters', 'hurricane protection'],
  Handyman: ['handy man', 'handymen'],
  'General Contractor': ['general contracting', 'gc', 'remodel', 'remodeling'],
  'House Cleaning': [
    'interior cleaning',
    'house/interior cleaning',
    'house interior cleaning',
    'cleaning',
    'housekeeping',
    'maid service',
    'maids',
    'residential cleaning',
    'home cleaning',
  ],
  'Window Cleaning': ['window washing', 'window washer'],
  'Pressure Washing': ['pressure wash', 'power washing', 'power wash', 'soft washing'],
  'Carpet Cleaning': ['carpet cleaner', 'rug cleaning'],
  'Air Duct Cleaning': ['air duct', 'air ducts', 'duct cleaning'],
  'Dryer Vent Cleaning': ['dryer vent', 'dryer vents'],
  Chimney: ['chimney sweep', 'chimney cleaning', 'fireplace'],
  Landscaping: ['landscaping/lawn care', 'lawn care', 'lawn service', 'lawn maintenance', 'lawn', 'yard care'],
  'Tree Service': ['tree trimming', 'tree removal', 'arborist', 'tree care', 'trees'],
  Irrigation: ['sprinkler', 'sprinklers', 'sprinkler system', 'irrigation system'],
  'Pool Service': ['pool', 'pools', 'pool cleaning', 'pool maintenance', 'pool repair'],
  'Pest Control': ['pest', 'pests', 'exterminator', 'termite', 'termites'],
  Fencing: ['fence', 'fences'],
  'Concrete and Pavers': ['concrete', 'pavers', 'paver', 'concrete/pavers', 'hardscape', 'hardscaping'],
  'Dock and Seawall': ['dock', 'docks', 'seawall', 'sea wall', 'dock/seawall'],
  'Screen Enclosures': ['screen enclosure', 'pool cage', 'lanai', 'screen repair'],
  Septic: ['septic system', 'septic tank'],
  'Well and Water Treatment': ['well', 'well water', 'water treatment', 'water softener', 'well/water treatment'],
  'Mold Remediation': ['mold', 'mould', 'mold removal'],
  'Water Damage Restoration': ['water damage', 'water restoration', 'water mitigation', 'flood restoration'],
  Solar: ['solar panels', 'solar panel', 'solar power'],
  Generator: ['generators', 'standby generator', 'whole house generator'],
  Moving: ['mover', 'movers', 'moving company'],
  'Junk Removal': ['junk', 'junk hauling', 'hauling', 'debris removal'],
  'Home Security': ['security system', 'home alarm', 'alarm system'],
  'Smart Home': ['home automation'],
  Locksmith: ['lock smith', 'locksmiths'],
  'Real Estate': ['realtor', 'real estate agent'],
};

const SETTLEMENT_SERVICE_CATEGORIES = [
  'home warranty',
  'insurance',
  'insurance and financial services',
  'financial services',
  'financial',
  'mortgage',
  'mortgage/lending',
  'lending',
  'lender',
  'lendor',
  'loan',
  'title',
  'title insurance',
  'settlement',
  'closing',
  'escrow',
  'inspection',
  'home inspection',
  'inspector',
  'appraisal',
  'appraiser',
];

function normalizeCategory(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Punctuation and the word "and" fold away so "Mortgage & Lending",
// "mortgage/lending", and "Concrete/Pavers" compare as the same phrase.
function looseCategoryKey(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const SETTLEMENT_EXACT = new Set(SETTLEMENT_SERVICE_CATEGORIES.map(looseCategoryKey));
const HOME_TRADE_EXACT = new Set(DEFAULT_VENDOR_CATEGORIES.map(looseCategoryKey));

const CATEGORY_BY_KEY = new Map();
function addCategoryKey(alias, canonical) {
  const key = looseCategoryKey(alias);
  if (!key || SETTLEMENT_EXACT.has(key)) return;
  if (CATEGORY_BY_KEY.has(key) && CATEGORY_BY_KEY.get(key) !== canonical) return;
  CATEGORY_BY_KEY.set(key, canonical);
}
DEFAULT_VENDOR_CATEGORIES.forEach((name) => addCategoryKey(name, name));
Object.keys(CATEGORY_SYNONYMS).forEach((canonical) => {
  CATEGORY_SYNONYMS[canonical].forEach((alias) => addCategoryKey(alias, canonical));
});

function categoryLabel(value) {
  if (typeof value === 'string') return value;
  return value && value.name ? value.name : '';
}

/**
 * Canonical category for a CSV cell or typed name.
 * Settlement-service names resolve to { settlement: true } and are not renamed.
 * A custom category wins only when it is not already one of the home services.
 */
function resolveVendorCategory(name, extras) {
  const key = looseCategoryKey(name);
  if (!key) return {};
  if (SETTLEMENT_EXACT.has(key)) return { settlement: true };
  const canonical = CATEGORY_BY_KEY.get(key);
  if (canonical) return { name: canonical };
  const list = extras || [];
  for (let i = 0; i < list.length; i += 1) {
    const extraName = String(categoryLabel(list[i]) || '').trim();
    if (!extraName || looseCategoryKey(extraName) !== key) continue;
    if (SETTLEMENT_EXACT.has(looseCategoryKey(extraName))) return { settlement: true };
    return { name: extraName };
  }
  return {};
}

const SETTLEMENT_VENDOR_NOTICE =
  "Some of your listings are hidden from clients because Pacarion doesn't list settlement-service providers.";

function isSettlementServiceCategory(name) {
  const key = looseCategoryKey(name);
  return Boolean(key) && SETTLEMENT_EXACT.has(key);
}

function isHomeTradeCategory(name) {
  const resolved = resolveVendorCategory(name);
  return Boolean(resolved.name) && HOME_TRADE_EXACT.has(looseCategoryKey(resolved.name));
}

function missingDefaultCategoryNames(rows) {
  const have = [];
  (rows || []).forEach((row) => {
    const key = looseCategoryKey(categoryLabel(row));
    if (key) have.push(key);
  });
  return DEFAULT_VENDOR_CATEGORIES.filter((name) => !have.some((key) => (
    key === looseCategoryKey(name) || CATEGORY_BY_KEY.get(key) === name
  )));
}

function sortVendorCategories(rows) {
  const rank = new Map(DEFAULT_VENDOR_CATEGORIES.map((name, index) => [looseCategoryKey(name), index]));
  const tail = DEFAULT_VENDOR_CATEGORIES.length;
  return (rows || []).slice().sort((a, b) => {
    const aKey = looseCategoryKey(categoryLabel(a));
    const bKey = looseCategoryKey(categoryLabel(b));
    const aRank = rank.has(aKey) ? rank.get(aKey) : tail;
    const bRank = rank.has(bKey) ? rank.get(bKey) : tail;
    if (aRank !== bRank) return aRank - bRank;
    return String(categoryLabel(a)).localeCompare(String(categoryLabel(b)));
  });
}

function withDefaultVendorCategories(rows) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  missingDefaultCategoryNames(list).forEach((name) => {
    list.push({ id: `default:${looseCategoryKey(name)}`, name });
  });
  return sortVendorCategories(list);
}

function categoryNames(vendor) {
  const names = [];
  if (!vendor) return names;
  if (vendor.category) names.push(vendor.category);
  if (Array.isArray(vendor.categories)) names.push(...vendor.categories);
  return names.map((name) => String(name || '').trim()).filter(Boolean);
}

/**
 * Categories loaded into the edit form.
 * The main category stays first. Extra categories are kept, not replaced by it.
 */
function categoriesForVendorForm(vendor) {
  if (!vendor) return [];
  const names = [];
  const add = (value) => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    const name = String(value || '').trim();
    if (name && !names.includes(name)) names.push(name);
  };
  add(vendor.category);
  add(vendor.categories);
  return names;
}

function isFinancialType(vendor) {
  return normalizeCategory(vendor && vendor.type) === 'financial';
}

/**
 * Hide a settlement-service category.
 * Type "financial" hides only when every category is empty or none of
 * them is a home-service category. A plumber or roofer stored as
 * type financial stays visible.
 */
function isSettlementServiceVendor(vendor) {
  if (!vendor) return false;
  const names = categoryNames(vendor);
  if (names.some((name) => isSettlementServiceCategory(name))) return true;
  if (!isFinancialType(vendor)) return false;
  if (names.length === 0) return true;
  return !names.some((name) => isHomeTradeCategory(name));
}

function offeredVendorCategories(categories) {
  return (categories || []).filter((category) => {
    const name = typeof category === 'string' ? category : category && category.name;
    return name && !isSettlementServiceCategory(name);
  });
}

/**
 * Bubbles for the vendor form. Catalog categories stay as they are.
 * A category already saved on the vendor, such as Generator, is included
 * even when it is not in that catalog, so it can be removed.
 */
function categoryBubbles(catalog, selected) {
  const offered = offeredVendorCategories(catalog);
  const known = new Set();
  offered.forEach((category) => {
    const name = String((category && category.name) || '').trim();
    if (name) known.add(name);
  });
  const extras = [];
  (selected || []).forEach((value) => {
    const name = String(value || '').trim();
    if (!name || known.has(name) || extras.indexOf(name) !== -1) return;
    extras.push(name);
  });
  return { offered, extras };
}

module.exports = {
  DEFAULT_VENDOR_CATEGORIES,
  SETTLEMENT_SERVICE_CATEGORIES,
  SETTLEMENT_VENDOR_NOTICE,
  isHomeTradeCategory,
  isSettlementServiceCategory,
  isSettlementServiceVendor,
  offeredVendorCategories,
  categoryBubbles,
  categoriesForVendorForm,
  normalizeCategory,
  looseCategoryKey,
  resolveVendorCategory,
  missingDefaultCategoryNames,
  sortVendorCategories,
  withDefaultVendorCategories,
};
